import React, { useEffect, useState } from "react";
import { fetchServices, fetchCarTypes } from "../api";
import { useNavigate } from "react-router-dom";
import SEO from './SEO';
import "bootstrap/dist/css/bootstrap.min.css";
import "../style/Rates.css";

const Rates = () => {
  const [rates, setRates] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState("MCO Airport to Magic Kingdom Hotels");
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([fetchServices(), fetchCarTypes()])
      .then(([servicesResponse, carTypesResponse]) => {
        setRates(servicesResponse.data);
        setCarTypes(carTypesResponse.data);
      })
      .catch((error) => console.error(error));
  }, []);

  // Group rates by service and map prices to car types
  const groupedRates = rates.reduce((acc, rate) => {
    if (!acc[rate.service_description]) {
      acc[rate.service_description] = {
        description: rate.service_description,
        prices: {},
      };
    }
    acc[rate.service_description].prices[rate.car_id] = rate.price;
    return acc;
  }, {});

  const handleBookingClick = (service, carType) => {
    navigate("/ReservationForm", { state: { service, carType } });
  };

  return (
    <>
      <SEO 
        title="Our Rates | Competitive Pricing for Premium Transportation"
        description="Get competitive rates for premium transportation services in Jacksonville. Transparent pricing for airport transfers, weddings, corporate events, and more. No hidden fees."
        keywords="Jacksonville transportation rates, limo prices, airport transfer cost, wedding transportation pricing, competitive rates, transparent pricing"
        canonical="/rates"
      />
      <div className="rates-container">
        <section className="rates-section">
          <h2 className="text-center mb-2">Our Rates</h2>
          <h6 className="text-center mb-3">
            Competitive pricing for unparalleled quality and service satisfaction.
          </h6>

        {/* Desktop/Tablets: Modern Card Layout */}
        <div className="rates-desktop">
          <div className="rates-card">
            <div className="rates-header">
              <div className="row text-center g-1">
                <div className="col-3 fw-bold rates-service-header"></div>
                {carTypes.map((carType) => (
                  <div className="col rates-car-header" key={carType.car_id}>
                    <div className="car-type-wrapper">
                      <img
                        src={`/images/${carType.car_type
                          .toLowerCase()
                          .replace(/\s+/g, "_")}.avif`}
                        alt={carType.car_type}
                        className="car-type-image"
                      />
                      <p className="car-type-name">{carType.car_type}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rates-body">
              {Object.values(groupedRates).map((rateGroup, index) => (
                <div
                  className={`rates-row row g-1 align-items-center ${
                    index % 2 === 0 ? "rates-row-even" : "rates-row-odd"
                  }`}
                  key={rateGroup.description}
                >
                  <div className="col-3 rates-service-cell">
                    {rateGroup.description}
                  </div>
                  {carTypes.map((carType) => (
                    <div className="col rates-price-cell" key={carType.car_id}>
                      {rateGroup.prices[carType.car_id] ? (
                        <span
                          className="rates-price-button"
                          role="button"
                          title="Click to Book"
                          onClick={() =>
                            handleBookingClick(
                              rateGroup.description,
                              carType.car_type
                            )
                          }
                        >
                          ${rateGroup.prices[carType.car_id]}
                        </span>
                      ) : (
                        <span className="rates-price-na">N/A</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile: Compact Dropdown Layout */}
        <div className="rates-mobile">
          <div className="mobile-destination-selector">
            <label htmlFor="destination-select" className="mobile-select-label">
              <i className="fas fa-map-marker-alt me-2"></i>
              Select Destination
            </label>
            <select
              id="destination-select"
              className="form-select mobile-destination-dropdown"
              value={selectedDestination}
              
              onChange={(e) => setSelectedDestination(e.target.value)}
            >
              <option value="">Choose a destination...</option>
              {Object.keys(groupedRates).map((destination) => (
                <option key={destination} value={destination}>
                  {destination}
                </option>
              ))}
            </select>
          </div>

          {selectedDestination && (
            <div className="selected-destination-card">
              <div className="destination-header">
                <h4>
                  <i className="fas fa-location-dot me-2"></i>
                  {selectedDestination}
                </h4>
              </div>
              <div className="car-options-grid">
                {carTypes.map((carType) => (
                  <div className="car-option-card" key={carType.car_id}>
                    <div className="car-option-image">
                      <img
                        src={`/images/${carType.car_type
                          .toLowerCase()
                          .replace(/\s+/g, "_")}.avif`}
                        alt={carType.car_type}
                        className="car-image"
                      />
                    </div>
                    <div className="car-option-details">
                      <h6 className="car-name">{carType.car_type}</h6>
                      <div className="car-price">
                        {groupedRates[selectedDestination]?.prices[carType.car_id] ? (
                          <button
                            className="price-book-btn"
                            onClick={() =>
                              handleBookingClick(selectedDestination, carType.car_type)
                            }
                          >
                            <span className="price-amount">
                              ${groupedRates[selectedDestination].prices[carType.car_id]}
                            </span>
                            <span className="book-text">Book Now</span>
                          </button>
                        ) : (
                          <span className="price-na">N/A</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
    </>
  );
};

export default Rates;
